import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import {
  formatDate,
  formatMoney,
  PROPERTY_SYSTEM_TYPE_LABELS,
} from "@mavatech/shared";
import { PageHeader } from "@/components/layout/Layout";
import {
  Badge,
  Button,
  Input,
  Select,
  SortTh,
  useSort,
  LoadError,
} from "@/components/ui";
import { trpc } from "@/lib/trpc";

const TONE: Record<
  string,
  "neutral" | "accent" | "success" | "warning" | "danger"
> = {
  draft: "neutral",
  sent: "accent",
  partially_paid: "warning",
  paid: "success",
  overdue: "danger",
  cancelled: "neutral",
};
const LABEL: Record<string, string> = {
  draft: "Entwurf",
  sent: "Offen",
  partially_paid: "Teilbezahlt",
  paid: "Bezahlt",
  overdue: "Überfällig",
  cancelled: "Storniert",
};
const KIND: Record<string, string> = {
  standalone: "Rechnung",
  advance: "Anzahlung",
  partial: "Teilrechnung",
  final: "Schlussrechnung",
  credit_note: "Gutschrift",
};

export function Invoices() {
  const navigate = useNavigate();
  const list = trpc.invoice.list.useQuery();

  const [search, setSearch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [systemType, setSystemType] = useState("");

  const properties = useMemo(() => {
    const map = new Map<string, { id: string; label: string }>();
    for (const i of list.data ?? []) {
      if (i.property)
        map.set(i.property.id, { id: i.property.id, label: i.property.label });
    }
    return [...map.values()];
  }, [list.data]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return (list.data ?? []).filter((i) => {
      if (s) {
        const hay = [
          i.number,
          i.title,
          i.customer.name,
          i.property?.addressStreet,
          i.property?.addressCity,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(s)) return false;
      }
      if (from && i.dueDate < new Date(from)) return false;
      if (to && i.dueDate > new Date(`${to}T23:59:59`)) return false;
      if (propertyId && i.property?.id !== propertyId) return false;
      if (systemType && i.property?.systemType !== systemType) return false;
      return true;
    });
  }, [list.data, search, from, to, propertyId, systemType]);

  const { sorted, sort, toggle } = useSort(filtered, {
    customer: (i) => i.customer.name,
    number: (i) => i.number,
    property: (i) => i.property?.label,
    due: (i) => i.dueDate,
    status: (i) => LABEL[i.status] ?? i.status,
    amount: (i) =>
      i.kind === "credit_note" ? i.grossCents : i.payableGrossCents,
    open: (i) =>
      i.kind === "credit_note"
        ? 0
        : Math.max(0, i.payableGrossCents - i.paidCents),
  });

  const hasFilters = search || from || to || propertyId || systemType;

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Rechnungen"
        actions={
          <Button variant="primary" onClick={() => navigate("/rechnungen/neu")}>
            <Plus className="w-4 h-4" /> Neue Rechnung
          </Button>
        }
      />
      <div className="px-6 py-3 border-b border-border-subtle flex items-center gap-3 flex-wrap">
        <div className="relative max-w-xs flex-1">
          <Search className="w-4 h-4 text-text-tertiary absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input
            className="pl-8"
            placeholder="Kunde, Betreff, Nummer, Adresse …"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Input
          fullWidth={false}
          className="w-40"
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          title="Fällig von"
        />
        <Input
          fullWidth={false}
          className="w-40"
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          title="Fällig bis"
        />
        <Select
          fullWidth={false}
          className="w-44"
          value={propertyId}
          onChange={(e) => setPropertyId(e.target.value)}
          options={[
            { value: "", label: "Alle Objekte" },
            ...properties.map((p) => ({ value: p.id, label: p.label })),
          ]}
        />
        <Select
          fullWidth={false}
          className="w-44"
          value={systemType}
          onChange={(e) => setSystemType(e.target.value)}
          options={[
            { value: "", label: "Alle Anlagentypen" },
            ...Object.entries(PROPERTY_SYSTEM_TYPE_LABELS).map(
              ([value, label]) => ({ value, label }),
            ),
          ]}
        />
        {hasFilters && (
          <button
            className="text-xs text-text-tertiary hover:text-text-primary"
            onClick={() => {
              setSearch("");
              setFrom("");
              setTo("");
              setPropertyId("");
              setSystemType("");
            }}
          >
            Filter zurücksetzen
          </button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto overflow-x-auto">
        {list.isLoading ? (
          <p className="p-6 text-sm text-text-tertiary">Lädt …</p>
        ) : list.isError ? (
          <LoadError onRetry={() => list.refetch()} />
        ) : !filtered.length ? (
          <div className="p-10 text-center text-sm text-text-tertiary">
            {hasFilters ? "Keine Treffer." : "Noch keine Rechnungen."}
          </div>
        ) : (
          <>
            {/* Mobil: Karten statt Tabelle — eine Tabelle mit 8 Spalten ist am Handy nur per
              Horizontal-Scroll nutzbar (AUDIT-2026-10-01-UX.md, Punkt 3.3). */}
            <div className="md:hidden divide-y divide-border-subtle">
              {sorted.map((i) => {
                const payable =
                  i.kind === "credit_note" ? i.grossCents : i.payableGrossCents;
                const openAmount =
                  i.kind === "credit_note"
                    ? 0
                    : Math.max(0, payable - i.paidCents);
                return (
                  <button
                    key={i.id}
                    onClick={() => navigate(`/rechnungen/${i.id}`)}
                    className="w-full text-left px-4 py-3 flex flex-col gap-1"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-xs text-text-tertiary">
                        {i.number ?? "—"}
                      </span>
                      <Badge tone={TONE[i.status] ?? "neutral"}>
                        {i.kind === "credit_note" && i.status === "paid"
                          ? "Ausgestellt"
                          : (LABEL[i.status] ?? i.status)}
                      </Badge>
                    </div>
                    <div className="font-medium text-text-primary">
                      {i.customer.name}
                    </div>
                    <div className="text-text-tertiary text-xs">{i.title}</div>
                    <div className="flex items-center justify-between gap-2 mt-1 text-sm">
                      <span className="text-text-tertiary text-xs">
                        Fällig {formatDate(i.dueDate as unknown as string)}
                      </span>
                      <span className="text-money text-text-primary">
                        {formatMoney(payable)}
                        {openAmount > 0 && (
                          <span className="text-text-tertiary">
                            {" "}
                            · offen {formatMoney(openAmount)}
                          </span>
                        )}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
            <table className="data-table hidden md:table">
              <thead>
                <tr>
                  <SortTh
                    label="Kunde / Betreff"
                    sortKey="customer"
                    sort={sort}
                    onSort={toggle}
                  />
                  <SortTh
                    label="Nummer"
                    sortKey="number"
                    sort={sort}
                    onSort={toggle}
                  />
                  <SortTh
                    label="Objekt"
                    sortKey="property"
                    sort={sort}
                    onSort={toggle}
                  />
                  <SortTh
                    label="Fällig"
                    sortKey="due"
                    sort={sort}
                    onSort={toggle}
                  />
                  <SortTh
                    label="Status"
                    sortKey="status"
                    sort={sort}
                    onSort={toggle}
                  />
                  <SortTh
                    label="Betrag"
                    sortKey="amount"
                    sort={sort}
                    onSort={toggle}
                    className="text-right"
                  />
                  <SortTh
                    label="Offen"
                    sortKey="open"
                    sort={sort}
                    onSort={toggle}
                    className="text-right"
                  />
                </tr>
              </thead>
              <tbody>
                {sorted.map((i) => {
                  const payable =
                    i.kind === "credit_note"
                      ? i.grossCents
                      : i.payableGrossCents;
                  // Gutschriften stellen dem Kunden nichts in Rechnung — der Betrag selbst bleibt
                  // sichtbar, aber "offen" ist dabei nichts.
                  const openAmount =
                    i.kind === "credit_note"
                      ? 0
                      : Math.max(0, payable - i.paidCents);
                  return (
                    <tr
                      key={i.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/rechnungen/${i.id}`)}
                    >
                      <td className="text-text-primary">
                        <div className="font-medium">{i.customer.name}</div>
                        <div className="text-text-tertiary text-xs">
                          {i.title}
                        </div>
                      </td>
                      <td className="font-mono text-text-secondary">
                        {i.number ?? "—"}
                        {/* Spalte "Art" entfällt; Anzahlung/Teilrechnung/Schluss/Gutschrift bleiben hier als Zusatz erkennbar */}
                        {i.kind !== "standalone" && (
                          <div className="font-sans text-2xs text-text-tertiary">
                            {KIND[i.kind] ?? i.kind}
                            {i.kind === "credit_note" &&
                            i.creditedInvoice?.number
                              ? ` zu ${i.creditedInvoice.number}`
                              : ""}
                          </div>
                        )}
                      </td>
                      <td className="text-text-tertiary text-xs">
                        {i.property?.label ?? "—"}
                      </td>
                      <td className="text-text-secondary">
                        {formatDate(i.dueDate as unknown as string)}
                      </td>
                      <td>
                        <Badge tone={TONE[i.status] ?? "neutral"}>
                          {i.kind === "credit_note" && i.status === "paid"
                            ? "Ausgestellt"
                            : (LABEL[i.status] ?? i.status)}
                        </Badge>
                      </td>
                      <td className="text-right text-money text-text-primary">
                        {formatMoney(payable)}
                      </td>
                      <td className="text-right text-money text-text-secondary">
                        {formatMoney(openAmount)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}
